import { ref, computed, watch } from 'vue';
import { useMutation } from '@tanstack/vue-query';
import * as PleasedApi from '@/common/api/api.pleased';
import * as PleasedModel from '@/common/api/models.pleased';
import { endResumableChatWithPresence } from '@/common/chat/endResumableChatPresence';
import { localStorageRemoveChatTriggersKeys } from '@/common/utils';
import { useRouter } from 'vue-router/composables';
import { useOpenChatsQuery } from '@/library/cf-v1/useOpenChatsQuery';
import { useMockedFlowStore } from '@/library/cf-v1/mocked-flow';
import { storeToRefs } from 'pinia';
import { devLog } from '@/common/debug';

export interface ResumableChatCallbacks {
  /**
   * Called when the user wants to resume the chat
   */
  onResume?: (chat: PleasedModel.ActiveOpenChat, canBeResumed: PleasedModel.VerifyChatOpenDto) => void;
  /**
   * Called when the user wants to go to active conversation
   */
  onActiveConversation?: (chat: PleasedModel.ActiveOpenChat) => void;
  /**
   * Called when the modal is closed
   */
  onClose?: () => void;
  /**
   * Called when the chat is ended
   */
  onEndChat?: () => void;
  /**
   * Called when the user wants to go to new chat
   */
  onNewChat?: () => void;
}

/**
 * Composable for handling resumable chat logic with TanStack Query
 * Provides functionality to check for open chats and manage the resumable chat modal
 *
 * @param callbacks - Optional callbacks for handling chat actions
 */
export const useResumableChat = (callbacks?: ResumableChatCallbacks) => {
  const router = useRouter();

  const mockedFlowStore = useMockedFlowStore();
  const {
    isActive: isMockedFlowActive,
    hasResumableChat: hasMockedResumableChat,
    resumableChat: mockedResumableChat,
    verifyChatOpen: mockedVerifyChatOpen,
  } = storeToRefs(mockedFlowStore);

  const showModal = ref(false);
  const currentOpenChat = ref<PleasedModel.ActiveOpenChat | null>(null);

  // Use shared open chats query
  const { openChats, isLoading: isFetchingChats, fetchAndCheck, invalidate } = useOpenChatsQuery();

  /**
   * Watch for query results and handle modal display
   */
  watch(
    () => openChats.value,
    (chat) => {
      if (!chat) {
        return;
      }

      currentOpenChat.value = chat;
      showModal.value = true;
    }
  );

  /**
   * When mocked resumable chat is set (e.g. switch to MODAL registry), show modal.
   * When it is cleared (switch away from MODAL), close modal.
   */
  watch(hasMockedResumableChat, (hasResumable) => {
    if (hasResumable && isMockedFlowActive.value && mockedResumableChat.value) {
      currentOpenChat.value = mockedResumableChat.value;
      showModal.value = true;
    } else if (!hasResumable) {
      showModal.value = false;
      currentOpenChat.value = null;
    }
  });

  /**
   * TanStack Mutation hook to end chat
   * Automatically handles loading state and cleanup
   */
  const { mutate: endChatMutation, isPending: isEndingChat } = useMutation({
    mutationFn: async (chat: PleasedModel.ActiveOpenChat) => {
      return await endResumableChatWithPresence(chat);
    },
    onSuccess: async () => {
      await invalidate();
      callbacks?.onEndChat?.();
      closeModal();
    },
  });

  /**
   * Check if there are any open chats and show modal if there are
   * Triggers the TanStack Query to fetch data
   * If in mocked mode, shows mocked resumable chat immediately
   */
  const checkOpenChats = async () => {
    // Check if we're in mocked mode and have mocked resumable chat
    if (isMockedFlowActive.value) {
      if (!hasMockedResumableChat.value) return;

      devLog.debug('[RESUMABLE_CHAT] Using mocked resumable chat data');
      currentOpenChat.value = mockedResumableChat.value;
      showModal.value = true;
      return;
    }

    // Otherwise, fetch from API
    await fetchAndCheck();
  };

  /**
   * Handle resume chat action
   */
  const handleResume = (resumableChat: PleasedModel.ActiveOpenChat, canBeResumed: PleasedModel.VerifyChatOpenDto) => {
    PleasedApi.checkChatCanBeResumed({ chatId: resumableChat.id });
    callbacks?.onResume?.(resumableChat, canBeResumed);
    closeModal();
  };

  /**
   * Handle end chat action
   * Uses TanStack Mutation for better state management
   */
  const handleEndChat = (resumableChat: PleasedModel.ActiveOpenChat) => {
    localStorageRemoveChatTriggersKeys();
    endChatMutation(resumableChat);
  };

  /**
   * Handle go to active conversation action
   */
  const handleActiveConversation = async (resumableChat: PleasedModel.ActiveOpenChat) => {
    callbacks?.onActiveConversation?.(resumableChat);
    router.push(`/conversation/${resumableChat.ticketId}`);
    closeModal();
  };

  /**
   * Handle go to my requests action
   */
  const handleMyRequests = () => {
    router.push('/conversations');
    closeModal();
  };

  /**
   * Handle new chat action (just closes the modal)
   */
  const handleNewChat = () => {
    callbacks?.onNewChat?.();
    closeModal();
  };

  /**
   * Close the modal and reset state
   */
  const closeModal = () => {
    showModal.value = false;
    currentOpenChat.value = null;
    callbacks?.onClose?.();
  };

  // Computed loading state that combines query loading and mutation loading
  const isLoading = computed(() => isFetchingChats.value || isEndingChat.value);

  return {
    // State
    showModal,
    currentOpenChat,
    isLoading,
    isEndingChat,

    // Methods
    checkOpenChats,
    handleResume,
    handleEndChat,
    handleActiveConversation,
    handleMyRequests,
    handleNewChat,
    closeModal,
  };
};
